import { apiService, ApiResponse } from './api';
import { 
  Discount, 
  CreateDiscountDto, 
  UpdateDiscountDto, 
  DiscountFilters,
  DiscountUsageStats,
  DiscountValidationResult,
  ApplyDiscountDto
} from '../types/discount';

class DiscountService {
  private baseEndpoint = '/api/discounts';

  async getAllDiscounts(filters?: DiscountFilters): Promise<ApiResponse<Discount[]>> {
    const queryParams = new URLSearchParams();
    
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.type) queryParams.append('type', filters.type);
    if (filters?.isPublic !== undefined) queryParams.append('isPublic', filters.isPublic.toString());
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
    if (filters?.search) queryParams.append('search', filters.search);

    const endpoint = queryParams.toString() 
      ? `${this.baseEndpoint}?${queryParams.toString()}`
      : this.baseEndpoint;

    return apiService.get<Discount[]>(endpoint);
  }

  async getDiscountById(id: string): Promise<ApiResponse<Discount>> {
    return apiService.get<Discount>(`${this.baseEndpoint}/${id}`);
  }

  async getDiscountByCode(code: string): Promise<ApiResponse<Discount>> {
    return apiService.get<Discount>(`${this.baseEndpoint}/code/${code}`);
  }

  async createDiscount(discountData: CreateDiscountDto): Promise<ApiResponse<Discount>> {
    return apiService.post<Discount>(this.baseEndpoint, discountData);
  }

  async updateDiscount(id: string, discountData: UpdateDiscountDto): Promise<ApiResponse<Discount>> {
    return apiService.patch<Discount>(`${this.baseEndpoint}/${id}`, discountData);
  }

  async deleteDiscount(id: string): Promise<ApiResponse<void>> {
    return apiService.delete<void>(`${this.baseEndpoint}/${id}`);
  }

  async activateDiscount(id: string): Promise<ApiResponse<Discount>> {
    return apiService.patch<Discount>(`${this.baseEndpoint}/${id}/activate`, {});
  }

  async deactivateDiscount(id: string): Promise<ApiResponse<Discount>> {
    return apiService.patch<Discount>(`${this.baseEndpoint}/${id}/deactivate`, {});
  }

  async expireDiscount(id: string): Promise<ApiResponse<Discount>> {
    return apiService.patch<Discount>(`${this.baseEndpoint}/${id}/expire`, {});
  }

  async validateDiscount(validationData: ApplyDiscountDto): Promise<ApiResponse<DiscountValidationResult>> {
    return apiService.post<DiscountValidationResult>(`${this.baseEndpoint}/validate`, validationData);
  }

  async applyDiscount(applyData: ApplyDiscountDto): Promise<ApiResponse<{
    success: boolean;
    discountAmount: number;
    finalAmount: number;
    discount: Discount;
  }>> {
    return apiService.post(`${this.baseEndpoint}/apply`, applyData);
  }

  async getDiscountUsageStats(id: string): Promise<ApiResponse<DiscountUsageStats>> {
    return apiService.get<DiscountUsageStats>(`${this.baseEndpoint}/${id}/stats`);
  }

  async getActiveDiscountsForCustomer(
    customerId: string, 
    carId?: string, 
    location?: string
  ): Promise<ApiResponse<Discount[]>> {
    const queryParams = new URLSearchParams();
    queryParams.append('customerId', customerId);
    if (carId) queryParams.append('carId', carId);
    if (location) queryParams.append('location', location);

    return apiService.get<Discount[]>(`${this.baseEndpoint}/active?${queryParams.toString()}`);
  }

  async generateDiscountCode(): Promise<string> {
    // Generate a random discount code
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  formatDiscountValue(discount: Discount): string {
    switch (discount.type) {
      case 'percentage':
        return `${discount.value}%`;
      case 'fixed_amount':
        return `$${discount.value.toFixed(2)}`;
      case 'free_days':
        return `${discount.value} free day${discount.value !== 1 ? 's' : ''}`;
      default:
        return discount.value.toString();
    }
  }

  getDiscountStatusColor(status: string): string {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'inactive':
        return 'text-gray-600 bg-gray-100';
      case 'expired':
        return 'text-red-600 bg-red-100';
      case 'used_up':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }

  isDiscountExpiringSoon(discount: Discount, daysThreshold: number = 7): boolean {
    const now = new Date();
    const validUntil = new Date(discount.validUntil);
    const timeDiff = validUntil.getTime() - now.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    return daysDiff <= daysThreshold && daysDiff > 0;
  }

  calculateUsagePercentage(discount: Discount): number {
    if (discount.usageLimit === 0) return 0; // Unlimited usage
    return Math.round((discount.usageCount / discount.usageLimit) * 100);
  }

  calculateBudgetPercentage(discount: Discount): number {
    if (discount.budgetLimit === 0) return 0; // Unlimited budget
    return Math.round((discount.budgetUsed / discount.budgetLimit) * 100);
  }
}

export const discountService = new DiscountService();